import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import DatePicker from "../../components/DatePicker";
import styles from "./CalendarPage.module.css";
import { getCurrentStoreName } from "../../utils/stores";
import { useCompanyDefaults } from "../../utils/companyDefaults";

/** Parse a leavesJson blob into { name, days, convertible } rows.
 *  Kept local to the Calendar feature to avoid a cross-feature util. */
function parseLeaveCredits(raw) {
  if (!raw) return [];
  try {
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(d)) return [];
    return d.map(o => ({
      name: String(o?.name || ""),
      days: Number(o?.days) || 0,
      convertible: !!o?.convertible,
    })).filter(l => l.name);
  } catch { return []; }
}

/** Merge default leaves into the employee's list by name (case-insensitive).
 *  Defaults the employee doesn't already have get appended so Settings
 *  edits show up live here too. */
function mergeLeaveCredits(empLeaves, defaultsRaw) {
  const defaults = parseLeaveCredits(defaultsRaw);
  if (defaults.length === 0) return empLeaves;
  const names = new Set(empLeaves.map(l => (l.name || "").trim().toLowerCase()));
  const missing = defaults.filter(d => !names.has((d.name || "").trim().toLowerCase()));
  return missing.length === 0 ? empLeaves : [...empLeaves, ...missing];
}

/** Build a Set of "YYYY-MM-DD" date keys covered by any Holiday /
 *  Regular Holiday / Special Holiday event in the list. Used by the
 *  leave-used counter below so holidays don't consume leave credits. */
function buildHolidayDateSet(events) {
  const out = new Set();
  if (!Array.isArray(events)) return out;
  for (const ev of events) {
    const t = (ev.eventType || "").toLowerCase();
    if (t !== "holiday" && t !== "regular holiday" && t !== "special holiday") continue;
    const hs = new Date(ev.startDate + "T00:00:00");
    const he = new Date((ev.endDate || ev.startDate) + "T00:00:00");
    if (isNaN(hs) || isNaN(he)) continue;
    const cur = new Date(hs);
    while (cur <= he) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, "0");
      const d = String(cur.getDate()).padStart(2, "0");
      out.add(`${y}-${m}-${d}`);
      cur.setDate(cur.getDate() + 1);
    }
  }
  return out;
}

/** Count days already consumed by this user's Leave events in the
 *  given year. Matches each event to a credit row by title (case
 *  insensitive). Events whose title doesn't match any credit are
 *  counted under "Other Leave". Weekends (Sat/Sun) and any date
 *  carrying a Holiday-type event are skipped — they aren't workdays
 *  so they don't draw from the leave balance. */
function countUsedLeaveDays(events, employeeCode, year, holidayDates) {
  const byName = {};
  if (!Array.isArray(events) || !employeeCode) return byName;
  const holidays = holidayDates instanceof Set ? holidayDates : buildHolidayDateSet(events);
  const inYearStart = new Date(year, 0, 1);
  const inYearEnd   = new Date(year, 11, 31);
  for (const ev of events) {
    if ((ev.eventType || "").toLowerCase() !== "leave") continue;
    if (ev.createdBy !== employeeCode) continue;
    // Only approved leaves draw from the balance. Legacy rows with
    // status = null predate the approval workflow and are treated as
    // historic approvals so existing usage isn't silently restored.
    if (ev.status != null && ev.status !== "APPROVED") continue;
    const start = new Date(ev.startDate + "T00:00:00");
    const end   = new Date((ev.endDate || ev.startDate) + "T00:00:00");
    if (isNaN(start) || isNaN(end)) continue;
    // Clip to current year so multi-year ranges don't over-count.
    const s = start < inYearStart ? inYearStart : start;
    const e = end   > inYearEnd   ? inYearEnd   : end;
    if (e < s) continue;
    let days = 0;
    const cur = new Date(s);
    while (cur <= e) {
      const dow = cur.getDay(); // 0 = Sun, 6 = Sat
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, "0");
      const d = String(cur.getDate()).padStart(2, "0");
      const key = `${y}-${m}-${d}`;
      if (dow !== 0 && dow !== 6 && !holidays.has(key)) days++;
      cur.setDate(cur.getDate() + 1);
    }
    if (days === 0) continue;
    const title = (ev.title || "Other Leave").trim();
    byName[title] = (byName[title] || 0) + days;
  }
  return byName;
}

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const EVENT_TYPES = [
  "Leave",
  "Regular Holiday",
  "Special Holiday",
  "Training",
  "Meeting",
  "Store Event",
  "Other",
];

// Admin cannot create "Leave" events — that type is for employees only.
const ADMIN_EVENT_TYPES = EVENT_TYPES.filter(t => t !== "Leave");
// Employees cannot create holidays — those are admin-only.
const EMPLOYEE_EVENT_TYPES = EVENT_TYPES.filter(
  t => t !== "Regular Holiday" && t !== "Special Holiday"
);

// Which types default to "affects salary" when selected
const SALARY_DEFAULTS = {
  Leave: true, Holiday: true, "Regular Holiday": true, "Special Holiday": true,
  Overtime: true,
  Training: false, Meeting: false, "Store Event": false, Other: false,
};

const EVENT_BG = {
  Leave:             "rgba(192,57,43,.25)",
  "Regular Holiday": "rgba(231,76,60,.30)",
  "Special Holiday": "rgba(230,126,34,.30)",
  Holiday:           "rgba(231,76,60,.25)",
  Training:          "rgba(155,89,182,.25)",
  Meeting:           "rgba(86,160,90,.25)",
  "Store Event":     "rgba(41,128,185,.25)",
  Overtime:          "rgba(52,152,219,.25)",
  Birthday:          "rgba(233,130,198,.25)",
  Other:             "rgba(149,165,166,.25)",
};

const EVENT_DOT = {
  Leave:             "#c0392b",
  "Regular Holiday": "#e74c3c",
  "Special Holiday": "#e67e22",
  Holiday:           "#e74c3c",
  Training:          "#9b59b6",
  Meeting:           "#56a05a",
  "Store Event":     "#2980b9",
  Overtime:          "#3498db",
  Birthday:          "#e982c6",
  Other:             "#95a5a6",
};

// ── Friendly time picker (Hour / Minute / AM-PM dropdowns) ──
function TimePicker({ value, onChange, label }) {
  let hr12 = "", min = "", ampm = "AM";
  if (value) {
    const [h, m] = value.split(":").map(Number);
    ampm = h >= 12 ? "PM" : "AM";
    hr12 = String(h % 12 || 12);
    min = String(m).padStart(2, "0");
  }
  function update(newHr, newMin, newAmpm) {
    const h = newHr === "" ? "" : newHr;
    const m = newMin === "" ? "00" : newMin;
    const ap = newAmpm || ampm;
    if (h === "") { onChange(""); return; }
    let h24 = parseInt(h, 10);
    if (ap === "PM" && h24 !== 12) h24 += 12;
    if (ap === "AM" && h24 === 12) h24 = 0;
    onChange(`${String(h24).padStart(2, "0")}:${m}`);
  }
  const hours = ["","1","2","3","4","5","6","7","8","9","10","11","12"];
  const minutes = ["00","15","30","45"];
  return (
    <div className={styles.field}>
      <label className={styles.label}>{label}</label>
      <div className={styles.timePickerRow}>
        <select className={styles.timeSelect} value={hr12}
                onChange={e => update(e.target.value, min, ampm)}>
          <option value="">--</option>
          {hours.filter(Boolean).map(h => <option key={h} value={h}>{h}</option>)}
        </select>
        <span className={styles.timeColon}>:</span>
        <select className={styles.timeSelect} value={min} disabled={!hr12}
                onChange={e => update(hr12, e.target.value, ampm)}>
          {minutes.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className={styles.timeAmpm} value={ampm} disabled={!hr12}
                onChange={e => update(hr12, min, e.target.value)}>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}

function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return `hsl(${Math.abs(h) % 360}, 55%, 50%)`;
}
function initials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return (parts.map(p => p[0]).join("")).toUpperCase();
}

function formatDate(d) {
  const days = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const dt = new Date(d + "T00:00:00");
  return `${days[dt.getDay()]}, ${MONTH_NAMES[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
}

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, "0")}${ampm}`;
}

// Build 7-column Mon-Sun grid
function buildGrid(year, month) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDow = (first.getDay() + 6) % 7; // Mon=0...Sun=6

  const cells = [];
  const prevDays = new Date(year, month, 0).getDate();

  // Previous month padding
  for (let i = startDow - 1; i >= 0; i--) {
    const d = prevDays - i;
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    cells.push({ day: d, month: pm, year: py, muted: true });
  }

  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, muted: false });
  }

  // Next month padding to fill 42 cells
  const nm = month === 11 ? 0 : month + 1;
  const ny = month === 11 ? year + 1 : year;
  let n = 1;
  while (cells.length < 42) {
    cells.push({ day: n++, month: nm, year: ny, muted: true });
  }

  return cells;
}

function dateKey(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

// Parse date that might come as "YYYY-MM-DD" string or [year, month, day] array from backend
function parseDate(d) {
  if (!d) return null;
  if (Array.isArray(d)) return `${d[0]}-${String(d[1]).padStart(2, "0")}-${String(d[2]).padStart(2, "0")}`;
  return d;
}

// ── Event Modal ──
function EventModal({ event, onClose, onSaved, allEmployees, isAdmin, currentUser, events, year }) {
  const availableTypes = isAdmin ? ADMIN_EVENT_TYPES : EMPLOYEE_EVENT_TYPES;
  const defaultType = availableTypes[0];
  const [form, setForm] = useState({
    title: "", startDate: "", endDate: "", startTime: "", endTime: "",
    eventType: defaultType, affectsSalary: SALARY_DEFAULTS[defaultType] ?? true,
    notes: "", attendees: [],
    _duration: "Whole Day",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [empSearch, setEmpSearch] = useState("");
  const [empOpen, setEmpOpen] = useState(false);
  const overlayRef = useRef(null);
  const dropdownRef = useRef(null);
  const isEdit = !!event;

  // Live company defaults so newly-added default leaves flow through
  // here without a reload, matching Settings → ProfilePanel behaviour.
  const companyDefaults = useCompanyDefaults();

  // Resolve the current user's leave credits. Leave events are
  // personal (admins can't create them) so we always look at the
  // creator's own employee record — employeeCode comes from the
  // logged-in session.
  const leaveCredits = useMemo(() => {
    const code = currentUser?.employeeCode;
    if (!code) return [];
    const emp = (allEmployees || []).find(e => e.code === code);
    const own = parseLeaveCredits(emp?.leavesJson);
    return mergeLeaveCredits(own, companyDefaults["work.defaultLeaves"]);
  }, [currentUser, allEmployees, companyDefaults]);

  const usedByTitle = useMemo(
    () => countUsedLeaveDays(events, currentUser?.employeeCode, year),
    [events, currentUser, year]
  );

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setEmpOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (event) {
      const att = event.attendees
        ? event.attendees.split(",").map(s => s.trim()).filter(Boolean)
        : [];
      const st = event.startTime || "";
      const et = event.endTime || "";
      let dur = "Custom";
      if (!st && !et) dur = "Whole Day";
      else if (st === "08:00" && et === "12:00") dur = "Morning";
      else if (st === "13:00" && et === "17:00") dur = "Afternoon";
      setForm({
        title: event.title || "",
        startDate: parseDate(event.startDate) || "",
        endDate: parseDate(event.endDate) || "",
        startTime: st,
        endTime: et,
        eventType: event.eventType || "Leave",
        affectsSalary: event.affectsSalary ?? SALARY_DEFAULTS[event.eventType] ?? false,
        notes: event.notes || "",
        attendees: att,
        _duration: dur,
      });
    } else {
      setForm({
        title: "", startDate: "", endDate: "", startTime: "", endTime: "",
        eventType: defaultType, affectsSalary: SALARY_DEFAULTS[defaultType] ?? true,
        notes: "", attendees: [],
        _duration: "Whole Day",
      });
    }
    setError("");
  }, [event]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function addAttendee(code) {
    if (!form.attendees.includes(code)) {
      set("attendees", [...form.attendees, code]);
    }
    setEmpSearch("");
    setEmpOpen(false);
  }

  function removeAttendee(code) {
    set("attendees", form.attendees.filter(c => c !== code));
  }

  async function save() {
    if (!form.title.trim()) { setError("Title is required."); return; }
    if (!form.startDate) { setError("Start date is required."); return; }

    setSaving(true); setError("");
    try {
      const body = {
        title: form.title.trim(),
        startDate: form.startDate,
        endDate: form.endDate || form.startDate,
        startTime: form.startTime || null,
        endTime: form.endTime || null,
        eventType: form.eventType,
        affectsSalary: form.affectsSalary,
        notes: form.notes.trim() || null,
        attendees: form.attendees.join(",") || null,
        // Stamp the event with the store that's active in the Topbar
        // so it only shows when that store is selected. Holidays and
        // birthdays seeded by the backend have storeName = null and
        // stay visible in every store.
        storeName: getCurrentStoreName() || null,
      };

      const url = isEdit
        ? `${API_BASE}/api/calendar/events/${event.id}`
        : `${API_BASE}/api/calendar/events`;

      await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      onSaved();
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <div className={styles.overlay} ref={overlayRef}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>{isEdit ? "Edit Event" : "Add Event"}</div>
          <button className={styles.modalClose} onClick={onClose}>✕</button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.field}>
            <label className={styles.label}>
              {form.eventType === "Leave" ? "Leave Type *" : "Title *"}
            </label>
            {form.eventType === "Leave" ? (() => {
              // Dropdown drawn from the employee's leavesJson (merged
              // with the company defaults). The title field still
              // stores the leave name, so the backend + used-days
              // tracking don't need to change. If we're editing a
              // legacy leave whose title isn't in the list, include it
              // verbatim so the form doesn't silently blank it out.
              const names = leaveCredits.map(l => l.name);
              const legacy = !!form.title && !names.includes(form.title);
              return (
                <select className={styles.input}
                        value={form.title}
                        onChange={e => set("title", e.target.value)}>
                  <option value="">— Select leave type —</option>
                  {legacy && (
                    <option value={form.title}>
                      {form.title} (legacy — not in your profile)
                    </option>
                  )}
                  {leaveCredits.map(l => {
                    const usedKey = Object.keys(usedByTitle).find(
                      k => k.trim().toLowerCase() === l.name.trim().toLowerCase()
                    );
                    const used = usedKey ? usedByTitle[usedKey] : 0;
                    const remaining = Math.max(0, l.days - used);
                    const exhausted = remaining === 0 && l.days > 0;
                    return (
                      <option key={l.name}
                              value={l.name}
                              disabled={exhausted}>
                        {l.name} — {remaining}/{l.days} days
                        {exhausted ? " (exhausted)" : ""}
                      </option>
                    );
                  })}
                </select>
              );
            })() : (
              <input className={styles.input} value={form.title}
                     onChange={e => set("title", e.target.value)}
                     placeholder="e.g. Quarterly Meeting" />
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Event Type</label>
            <select className={styles.select} value={form.eventType} onChange={e => {
              const t = e.target.value;
              const personal = ["Leave", "Holiday", "Overtime", "Regular Holiday", "Special Holiday"].includes(t);
              setForm(f => ({
                ...f,
                eventType: t,
                affectsSalary: SALARY_DEFAULTS[t] ?? false,
                attendees: personal ? [] : f.attendees,
                // Switching TO Leave: clear any carried-over free-text
                // title so it doesn't render as a "legacy" dropdown
                // option. Leave → anything else keeps the previous
                // value since a leave name is still a reasonable title.
                title: (t === "Leave" && f.eventType !== "Leave") ? "" : f.title,
              }));
            }}>
              {availableTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>

            {/* Leave credits inline card — always rendered when Leave
                is selected so employees see their balance at a glance.
                Gold-tinted panel + bold header for discoverability. */}
            {form.eventType === "Leave" && (
              <div style={{
                marginTop: 10,
                padding: "12px 14px",
                background: "#fffaeb",
                border: "1px solid #f2d586",
                borderRadius: 10,
                boxShadow: "0 1px 0 rgba(0,0,0,.02)",
              }}>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  marginBottom: 8,
                }}>
                  <div style={{
                    fontSize: 12, fontWeight: 800, color: "#78350f",
                    letterSpacing: ".3px", display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <span style={{ fontSize: 14 }}>🏖</span>
                    Your leave credits ({year})
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    background: "#fde68a", color: "#78350f",
                    padding: "2px 8px", borderRadius: 20,
                  }}>
                    {leaveCredits.length} type{leaveCredits.length === 1 ? "" : "s"}
                  </span>
                </div>

                {!currentUser?.employeeCode ? (
                  <div style={{ fontSize: 12, color: "rgba(0,0,0,.55)" }}>
                    Your account isn't linked to an employee record, so there
                    are no credits to show. Ask an admin to link your user in
                    Settings → Users.
                  </div>
                ) : leaveCredits.length === 0 ? (
                  <div style={{ fontSize: 12, color: "rgba(0,0,0,.55)" }}>
                    No leave credits set up yet. Ask an admin to configure
                    defaults in Settings → Employee Defaults or add them to
                    your employee record in People.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {leaveCredits.map(l => {
                      const usedKey = Object.keys(usedByTitle).find(
                        k => k.trim().toLowerCase() === l.name.trim().toLowerCase()
                      );
                      const used = usedKey ? usedByTitle[usedKey] : 0;
                      const remaining = Math.max(0, l.days - used);
                      const exhausted = remaining === 0 && l.days > 0;
                      return (
                        <div key={l.name} style={{
                          display: "flex", alignItems: "center",
                          justifyContent: "space-between",
                          fontSize: 12,
                          padding: "4px 0",
                          borderBottom: "1px dotted rgba(0,0,0,.08)",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontWeight: 600, color: "#333" }}>{l.name}</span>
                            {l.convertible && (
                              <span style={{
                                fontSize: 9, background: "#fde68a", color: "#78350f",
                                padding: "1px 5px", borderRadius: 6, fontWeight: 700,
                              }}>CASH</span>
                            )}
                          </div>
                          <div style={{
                            fontSize: 12, fontWeight: 700,
                            color: exhausted ? "#991b1b"
                                  : remaining < l.days ? "#78350f"
                                  : "#14532d",
                          }}>
                            {remaining} / {l.days} days
                            {used > 0 && (
                              <span style={{ color: "rgba(0,0,0,.45)", fontWeight: 400, marginLeft: 4 }}>
                                ({used} used)
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ fontSize: 10, color: "rgba(0,0,0,.45)", marginTop: 4 }}>
                      "Used" = existing Leave events you created this year
                      against the same leave type. Pick your leave type from
                      the dropdown above — the balance updates once an admin
                      or your manager approves the request.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label}>Start Date *</label>
              <DatePicker value={form.startDate || ""} onChange={v => set("startDate", v)} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>End Date</label>
              <DatePicker value={form.endDate || ""} onChange={v => set("endDate", v)} />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Duration</label>
            <div className={styles.durationRow}>
              {[
                { label: "Whole Day", start: "", end: "" },
                { label: "Morning", start: "08:00", end: "12:00" },
                { label: "Afternoon", start: "13:00", end: "17:00" },
                { label: "Custom", start: null, end: null },
              ].map(opt => {
                const isActive = form._duration === opt.label;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    className={`${styles.durationBtn} ${isActive ? styles.durationActive : ""}`}
                    onClick={() => {
                      if (opt.start === null) {
                        setForm(f => ({ ...f, _duration: "Custom", startTime: "", endTime: "" }));
                      } else {
                        setForm(f => ({ ...f, _duration: opt.label, startTime: opt.start, endTime: opt.end }));
                      }
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {form._duration === "Custom" && (
            <div className={styles.fieldRow}>
              <TimePicker label="Start Time" value={form.startTime} onChange={v => set("startTime", v)} />
              <TimePicker label="End Time" value={form.endTime} onChange={v => set("endTime", v)} />
            </div>
          )}

          <div className={styles.field}>
            <label className={styles.label}>Notes</label>
            <textarea className={styles.textarea} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Add notes..." />
          </div>

          {/* Attendees — hidden for Leave & Overtime (personal events, just the creator) */}
          {!["Leave", "Holiday", "Overtime", "Regular Holiday", "Special Holiday"].includes(form.eventType) && (
            <div className={styles.field}>
              <label className={styles.label}>Attendees</label>
              <div className={styles.attendeeDropdown} ref={dropdownRef}>
                <div
                  className={styles.attendeeSelectBtn}
                  onClick={() => { setEmpOpen(o => !o); setEmpSearch(""); }}
                >
                  {form.attendees.length === 0
                    ? <span className={styles.attendeePlaceholder}>Select employees...</span>
                    : <span className={styles.attendeeCount}>{form.attendees.length} selected</span>
                  }
                  <span className={styles.attendeeChevron}>{empOpen ? "▴" : "▾"}</span>
                </div>
                {empOpen && (
                  <div className={styles.attendeeDropdownList}>
                    <div className={styles.attendeeSearchWrap}>
                      <input
                        className={styles.attendeeSearchInput}
                        value={empSearch}
                        onChange={e => setEmpSearch(e.target.value)}
                        placeholder="Search..."
                        autoFocus
                      />
                    </div>
                    <div className={styles.attendeeListScroll}>
                      {allEmployees
                        .filter(e => (e.name || "").toLowerCase().includes(empSearch.toLowerCase()))
                        .map(e => {
                          const checked = form.attendees.includes(e.code);
                          return (
                            <label key={e.code} className={styles.attendeeCheckItem}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => checked ? removeAttendee(e.code) : addAttendee(e.code)}
                              />
                              <span className={styles.attendeeCheckName}>{e.name}</span>
                            </label>
                          );
                        })}
                      {allEmployees.filter(e => (e.name || "").toLowerCase().includes(empSearch.toLowerCase())).length === 0 && (
                        <div className={styles.attendeeNoResult}>No employees found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {form.attendees.length > 0 && (
                <div className={styles.attendeePicker}>
                  {form.attendees.map(code => {
                    const emp = allEmployees.find(e => e.code === code);
                    return (
                      <span key={code} className={styles.attendeeTag}>
                        {emp?.name || code}
                        <button className={styles.attendeeTagRemove} onClick={() => removeAttendee(code)}>✕</button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {error && <div className={styles.modalError}>{error}</div>}

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.saveBtn} onClick={save} disabled={saving}>
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Event"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}

// ── Main CalendarPage ──
export default function CalendarPage() {
  const user = getUser();
  const isAdmin = user?.role === "Admin"
               || user?.role === "SuperAdmin"
               || user?.role === "Owner";
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [attendeeDetails, setAttendeeDetails] = useState([]);
  const [modalEvent, setModalEvent] = useState(undefined);
  const [allEmployees, setAllEmployees] = useState([]);
  const [monthDropOpen, setMonthDropOpen] = useState(false);
  const [yearPickerMode, setYearPickerMode] = useState(false);
  const [yearPageStart, setYearPageStart] = useState(today.getFullYear() - 5);
  const monthDropRef = useRef(null);
  const [selectedCode, setSelectedCode] = useState(isAdmin ? "" : (user?.employeeCode || ""));
  const [empSearch, setEmpSearch] = useState("");
  const [hideAdmin, setHideAdmin] = useState(false);

  // Derived list of employees who report to the current user (via
  // Employee.reportsTo). A non-admin user with at least one report is
  // treated as a manager — they get the employee sidebar scoped to
  // their team only.
  const myReports = useMemo(
    () => (user?.employeeCode
      ? (allEmployees || []).filter(e => e.reportsTo === user.employeeCode)
      : []),
    [allEmployees, user?.employeeCode]
  );
  const isManager = !isAdmin && myReports.length > 0;
  const showSidebar = isAdmin || isManager;

  // Leave-approval UI state.
  const [decisionNote,    setDecisionNote]    = useState("");
  const [decisionSaving,  setDecisionSaving]  = useState(false);
  const [recordsOpen,     setRecordsOpen]     = useState(false);
  const [records,         setRecords]         = useState([]);
  const [recordsLoading,  setRecordsLoading]  = useState(false);
  const [pendingCount,    setPendingCount]    = useState(0);

  // Toast shown when the operator opens a record whose applicant isn't
  // on their visible team (e.g. a manager whose reports list doesn't
  // include the applicant). Carries the applicant name + an optional
  // dismiss timer so the toast self-clears.
  const [teamWarning, setTeamWarning] = useState(null);
  useEffect(() => {
    if (!teamWarning) return undefined;
    const id = setTimeout(() => setTeamWarning(null), 6000);
    return () => clearTimeout(id);
  }, [teamWarning]);

  // True if `user` is the manager for the given applicant code — i.e.
  // the applicant's Employee.reportsTo equals the current user's code,
  // or the applicant's reports_to is a role token ("Admin" / "Owner")
  // that matches the caller's role.
  function isManagerOfApplicant(applicantCode) {
    if (!applicantCode || !user) return false;
    const applicant = allEmployees.find(e => e.code === applicantCode);
    if (!applicant) return false;
    const reports = applicant.reportsTo;
    if (!reports) return false;
    if (user.employeeCode && reports === user.employeeCode) return true;
    if (reports === "Admin"
        && (user.role === "Admin" || user.role === "Owner" || user.role === "SuperAdmin")) return true;
    if (reports === "Owner"
        && (user.role === "Owner" || user.role === "SuperAdmin")) return true;
    return false;
  }

  // Admin-tier can always decide/delete; otherwise the applicant's
  // manager gets the same rights on Leave events.
  function canDecide(ev) {
    if (!ev || ev.eventType !== "Leave") return false;
    if (isAdmin) return true;
    return isManagerOfApplicant(ev.createdBy);
  }

  async function handleDecide(status) {
    if (!selectedEvent?.id) return;
    setDecisionSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/calendar/events/${selectedEvent.id}/decision`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, note: decisionNote }),
      });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      setSelectedEvent(saved);
      setDecisionNote("");
      loadEvents();
      loadApprovals(); // refresh pending count + records
      // Notify the Topbar approvals bell so a decided leave drops off
      // (or surfaces in the requester's "your leave was approved" feed)
      // without waiting on the 60s poll.
      window.dispatchEvent(new CustomEvent("approvals:changed"));
    } catch {
      alert("Failed to save decision. Please try again.");
    } finally {
      setDecisionSaving(false);
    }
  }

  async function loadApprovals() {
    try {
      const res = await fetch(`${API_BASE}/api/calendar/events/approvals`);
      if (!res.ok) { setRecords([]); setPendingCount(0); return; }
      const rows = await res.json();
      const arr = Array.isArray(rows) ? rows : [];
      setRecords(arr);
      setPendingCount(arr.filter(r => r.status === "PENDING").length);
    } catch {
      setRecords([]); setPendingCount(0);
    }
  }

  useEffect(() => {
    if (user?.employeeCode || isAdmin) loadApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh the pending count any time the events list refreshes so
  // a brand-new leave request surfaces without waiting on polling.
  useEffect(() => {
    if (user?.employeeCode || isAdmin) loadApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.length]);

  // Clear the note textarea whenever the selected event changes so
  // the manager doesn't accidentally reuse a note from a prior event.
  useEffect(() => { setDecisionNote(""); }, [selectedEvent?.id]);

  // Map of employeeCode → count of PENDING leave requests so the
  // sidebar can flag which employees still need a decision.
  const pendingByEmp = useMemo(() => {
    const m = new Map();
    for (const r of (records || [])) {
      if (r.status !== "PENDING" || !r.createdBy) continue;
      m.set(r.createdBy, (m.get(r.createdBy) || 0) + 1);
    }
    return m;
  }, [records]);

  function loadEvents() {
    const params = new URLSearchParams({ year, month: month + 1 });
    if (selectedCode) params.set("employeeCode", selectedCode);
    const store = getCurrentStoreName();
    if (store) params.set("storeName", store);
    fetch(`${API_BASE}/api/calendar/events?${params}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]));
  }

  useEffect(() => { loadEvents(); }, [year, month, selectedCode]);

  useEffect(() => {
    // Attendee picker is scoped to the current store so admins only
    // see employees from whichever store the Topbar dropdown shows.
    // Non-store-scoped behaviour (all employees) returns when no
    // store is selected.
    const store = getCurrentStoreName();
    const qs    = store
      ? `?all=true&storeName=${encodeURIComponent(store)}`
      : `?all=true`;
    fetch(`${API_BASE}/api/employees${qs}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setAllEmployees(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Load attendee details when event selected
  useEffect(() => {
    if (!selectedEvent?.id) { setAttendeeDetails([]); return; }
    fetch(`${API_BASE}/api/calendar/events/${selectedEvent.id}/attendees`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setAttendeeDetails(Array.isArray(data) ? data : []))
      .catch(() => setAttendeeDetails([]));
  }, [selectedEvent]);

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  }
  function goToday() {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  }

  // Close month dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (monthDropRef.current && !monthDropRef.current.contains(e.target)) {
        setMonthDropOpen(false);
        setYearPickerMode(false);
      }
    }
    if (monthDropOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [monthDropOpen]);

  const cells = useMemo(() => buildGrid(year, month), [year, month]);

  // Set of "YYYY-MM-DD" date keys covered by the currently-selected event.
  // Drives the gold ring + amber background on day cells so the user can
  // see at a glance WHICH day(s) the leave they're about to approve covers.
  // Empty set when no event is selected — cells render normally then.
  const selectedEventDays = useMemo(() => {
    const out = new Set();
    if (!selectedEvent || !selectedEvent.startDate) return out;
    const start = new Date(selectedEvent.startDate + "T00:00:00");
    const end   = new Date((selectedEvent.endDate || selectedEvent.startDate) + "T00:00:00");
    if (isNaN(start) || isNaN(end)) return out;
    const cur = new Date(start);
    while (cur <= end) {
      const k = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,"0")}-${String(cur.getDate()).padStart(2,"0")}`;
      out.add(k);
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [selectedEvent]);

  // When the user picks an event from the Records modal that lives in a
  // different month, jump the calendar there so the highlighted cells
  // are actually visible. We only nudge when the event's month is
  // outside the current view — clicking an event already in view stays
  // in view (no jarring "snap to start of leave"). Run-once-per-event
  // via the id check.
  const navHandledFor = useRef(null);
  useEffect(() => {
    if (!selectedEvent || !selectedEvent.startDate) return;
    if (navHandledFor.current === selectedEvent.id) return;
    const d = new Date(selectedEvent.startDate + "T00:00:00");
    if (isNaN(d)) return;
    navHandledFor.current = selectedEvent.id;
    if (d.getFullYear() !== year || d.getMonth() !== month) {
      setYear(d.getFullYear());
      setMonth(d.getMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.id]);

  // Map events to grid cell indices + assign consistent slots per row
  const { eventsByIdx, slotsByRow } = useMemo(() => {
    const map = {};
    let filtered = events;
    if (hideAdmin) {
      filtered = filtered.filter(e => e.createdBy && e.createdBy !== "");
    }
    if (search.trim()) {
      filtered = filtered.filter(e => {
        const creatorName = (allEmployees.find(emp => emp.code === e.createdBy)?.name || "").toLowerCase();
        return creatorName.includes(search.toLowerCase());
      });
    }

    // Build lookup: "YYYY-MM-DD" -> grid cell index
    // Use only the FIRST occurrence of each dateKey (prev-month padding may share dates)
    const dateToIdx = {};
    cells.forEach((c, i) => {
      const k = dateKey(c.year, c.month, c.day);
      if (!(k in dateToIdx)) dateToIdx[k] = i;
    });

    // Set of every date that carries a holiday event. Used so Leave
    // events don't paint cells the employee wouldn't have worked
    // anyway (weekends + holidays). Built from the full event list so
    // a search filter doesn't accidentally un-suppress them.
    const holidayDates = new Set();
    for (const ev of events) {
      if (ev.eventType !== "Holiday"
          && ev.eventType !== "Regular Holiday"
          && ev.eventType !== "Special Holiday") continue;
      const hs = parseDate(ev.startDate);
      const he = parseDate(ev.endDate) || hs;
      const cur = new Date(hs + "T00:00:00");
      const stop = new Date(he + "T00:00:00");
      while (cur <= stop) {
        holidayDates.add(dateKey(cur.getFullYear(), cur.getMonth(), cur.getDate()));
        cur.setDate(cur.getDate() + 1);
      }
    }

    for (const ev of filtered) {
      const sd = parseDate(ev.startDate);
      const ed = parseDate(ev.endDate) || sd;
      const start = new Date(sd + "T00:00:00");
      const end = new Date(ed + "T00:00:00");
      const isMultiDay = start.getTime() !== end.getTime();
      const isLeave = ev.eventType === "Leave";

      // Collect all grid indices this event touches. For Leave events,
      // skip Saturdays, Sundays and holiday dates — the employee
      // wouldn't have been working those days, so painting a Leave
      // block on them would be misleading.
      const touchedIndices = [];
      const cur = new Date(start);
      while (cur <= end) {
        const k = dateKey(cur.getFullYear(), cur.getMonth(), cur.getDate());
        const dow = cur.getDay(); // 0 = Sun, 6 = Sat
        const skip = isLeave && (dow === 0 || dow === 6 || holidayDates.has(k));
        if (!skip) {
          const idx = dateToIdx[k];
          if (idx !== undefined) touchedIndices.push(idx);
        }
        cur.setDate(cur.getDate() + 1);
      }

      // If a Leave event lands entirely on skipped days, nothing to paint.
      if (touchedIndices.length === 0) continue;

      // Group touched cells into contiguous segments so each segment
      // around a weekend / holiday gap gets its own rounded start + end
      // rather than rendering as a single bar with holes.
      const segments = [];
      for (const idx of touchedIndices) {
        const last = segments[segments.length - 1];
        if (last && idx === last[last.length - 1] + 1) last.push(idx);
        else segments.push([idx]);
      }

      for (const seg of segments) {
        const firstIdx = seg[0];
        const lastIdx  = seg[seg.length - 1];
        for (const idx of seg) {
          if (!map[idx]) map[idx] = [];
          map[idx].push({
            ...ev,
            isStart: idx === firstIdx,
            isEnd:   idx === lastIdx,
            isMultiDay,
          });
        }
      }
    }

    // Assign consistent slot (vertical lane) per event within each row
    const totalRows = Math.ceil(cells.length / 7);
    const rowSlots = {};

    for (let r = 0; r < totalRows; r++) {
      const startIdx = r * 7;
      const endIdx = Math.min(startIdx + 7, cells.length);
      const slots = {};
      let nextSlot = 0;

      const seen = new Set();
      for (let i = startIdx; i < endIdx; i++) {
        const evts = map[i] || [];
        for (const ev of evts) {
          if (!seen.has(ev.id)) {
            seen.add(ev.id);
            slots[ev.id] = nextSlot++;
          }
        }
      }
      rowSlots[r] = slots;
    }

    return { eventsByIdx: map, slotsByRow: rowSlots };
  }, [events, search, cells, allEmployees, hideAdmin]);

  const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate());

  async function handleDelete() {
    if (!selectedEvent?.id) return;
    const label = selectedEvent.eventType === "Leave"
      ? `${selectedEvent.title || "Leave"} (${selectedEvent.startDate}${selectedEvent.endDate && selectedEvent.endDate !== selectedEvent.startDate ? " → " + selectedEvent.endDate : ""})`
      : `"${selectedEvent.title || "this event"}"`;
    if (!window.confirm(`Delete ${label}?\nThis cannot be undone.`)) return;
    await fetch(`${API_BASE}/api/calendar/events/${selectedEvent.id}`, { method: "DELETE" });
    setSelectedEvent(null);
    loadEvents();
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageTitle}>Calendar</div>
        <div className={styles.headerRight}>
          {isAdmin && (
            <button className={styles.filterBtn} title="Filter">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" width="16" height="16">
                <path d="M3 5h14M6 10h8M9 15h2"/>
              </svg>
            </button>
          )}
          {(user?.employeeCode || isAdmin) && (
            <button
              className={styles.recordsBtn}
              title="Leave approval records"
              onClick={() => setRecordsOpen(true)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 3h6a2 2 0 0 1 2 2v0H7v0a2 2 0 0 1 2-2z"/>
                <rect x="5" y="5" width="14" height="16" rx="2"/>
                <path d="M9 11h6M9 15h6"/>
              </svg>
              Records
              {pendingCount > 0 && (
                <span className={styles.recordsBtnBadge}>{pendingCount}</span>
              )}
            </button>
          )}
          <button className={styles.addEventBtn} onClick={() => setModalEvent(null)}>
            + Add Event
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={`${styles.content} ${showSidebar ? styles.contentWithSidebar : ""}`}>
        {/* Employee sidebar: admin-tier sees everyone, manager sees
            just their direct reports (plus themselves at the top). */}
        {showSidebar && (
          <div className={styles.sidebar}>
            <div className={styles.sideSearch}>
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" width="14" height="14">
                <circle cx="9" cy="9" r="6"/><path d="M15 15l-3.5-3.5"/>
              </svg>
              <input
                className={styles.sideSearchInput}
                placeholder="Search employee"
                value={empSearch}
                onChange={e => setEmpSearch(e.target.value)}
              />
            </div>
            <div className={styles.sideList}>
              {isAdmin && (
                <div
                  className={`${styles.sideItem} ${selectedCode === "" ? styles.sideItemActive : ""}`}
                  onClick={() => { setSelectedCode(""); setSelectedEvent(null); setSelectedDay(null); }}
                >
                  <div className={styles.sideAvatarFallback} style={{ background: "#c9a227" }}>All</div>
                  <div className={styles.sideName}>All Employees</div>
                </div>
              )}
              {isManager && user?.employeeCode && (() => {
                // "Me" row at the top so the manager can flip back to
                // their own calendar without scrolling through reports.
                const self = allEmployees.find(e => e.code === user.employeeCode);
                if (!self) return null;
                return (
                  <div
                    className={`${styles.sideItem} ${selectedCode === self.code ? styles.sideItemActive : ""}`}
                    onClick={() => { setSelectedCode(self.code); setSelectedEvent(null); setSelectedDay(null); }}>
                    {self.photoUrl
                      ? <img src={self.photoUrl} alt={self.name} className={styles.sideAvatar} />
                      : <div className={styles.sideAvatarFallback} style={{ background: avatarColor(self.name || "?") }}>
                          {initials(self.name || "?")}
                        </div>
                    }
                    <div>
                      <div className={styles.sideName}>{self.name} (You)</div>
                      <div className={styles.sideRole}>{self.role || ""}</div>
                    </div>
                  </div>
                );
              })()}
              {(isAdmin ? allEmployees : myReports)
                .filter(e => (e.name || "").toLowerCase().includes(empSearch.toLowerCase()))
                .map(emp => {
                  const pending = pendingByEmp.get(emp.code) || 0;
                  return (
                    <div
                      key={emp.code}
                      className={`${styles.sideItem} ${selectedCode === emp.code ? styles.sideItemActive : ""} ${pending > 0 ? styles.sideItemPending : ""}`}
                      onClick={() => { setSelectedCode(emp.code); setSelectedEvent(null); setSelectedDay(null); }}
                      title={pending > 0 ? `${pending} pending approval${pending > 1 ? "s" : ""}` : undefined}
                    >
                      {emp.photoUrl
                        ? <img src={emp.photoUrl} alt={emp.name} className={styles.sideAvatar} />
                        : <div className={styles.sideAvatarFallback} style={{ background: avatarColor(emp.name || "?") }}>
                            {initials(emp.name || "?")}
                          </div>
                      }
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className={styles.sideName}>{emp.name}</div>
                        <div className={styles.sideRole}>{emp.role || ""}</div>
                      </div>
                      {pending > 0 && (
                        <span className={styles.sidePendingBadge}>{pending}</span>
                      )}
                    </div>
                  );
                })
              }
            </div>
          </div>
        )}

        {/* Calendar card */}
        <div className={styles.calendarCard}>
          <div className={styles.calToolbar}>
            <div className={styles.searchWrap}>
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="9" cy="9" r="6"/><path d="M15 15l-3.5-3.5"/>
              </svg>
              <input className={styles.searchInput} placeholder="Search by employee name" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button
              className={`${styles.hideAdminBtn} ${hideAdmin ? styles.hideAdminActive : ""}`}
              onClick={() => setHideAdmin(h => !h)}
              title={hideAdmin ? "Show admin events" : "Hide admin events"}
            >
              {hideAdmin ? "Admin Hidden" : "Hide Admin"}
            </button>
            <div className={styles.monthNav}>
              <button className={styles.navArrow} onClick={prevMonth}>&#8249;</button>
              <div className={styles.monthPickerWrap} ref={monthDropRef}>
                <div className={styles.monthLabel} onClick={() => setMonthDropOpen(o => !o)}>
                  {MONTH_NAMES[month]} {year} <span style={{ fontSize: 10, marginLeft: 2 }}>&#9662;</span>
                </div>
                {monthDropOpen && (
                  <div className={styles.monthDropdown}>
                    <div className={styles.monthDropHeader}>
                      <button
                        className={styles.monthDropArrow}
                        onClick={() => yearPickerMode
                          ? setYearPageStart(s => s - 12)
                          : setYear(y => y - 1)}
                        title={yearPickerMode ? "Previous years" : "Previous year"}
                      >&#8249;</button>
                      <button
                        className={styles.monthDropYearLabel}
                        onClick={() => {
                          setYearPickerMode(m => !m);
                          setYearPageStart(year - 5);
                        }}
                        title="Switch to year picker"
                      >
                        {yearPickerMode
                          ? `${yearPageStart} — ${yearPageStart + 11}`
                          : year}
                        <span className={styles.monthDropYearChev}>&#9662;</span>
                      </button>
                      <button
                        className={styles.monthDropArrow}
                        onClick={() => yearPickerMode
                          ? setYearPageStart(s => s + 12)
                          : setYear(y => y + 1)}
                        title={yearPickerMode ? "Next years" : "Next year"}
                      >&#8250;</button>
                    </div>

                    {yearPickerMode ? (
                      <div className={styles.monthDropGrid}>
                        {Array.from({ length: 12 }, (_, i) => yearPageStart + i).map(y => (
                          <button
                            key={y}
                            className={`${styles.monthDropItem} ${y === year ? styles.monthDropActive : ""} ${y === today.getFullYear() ? styles.monthDropToday : ""}`}
                            onClick={() => { setYear(y); setYearPickerMode(false); }}
                          >
                            {y}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.monthDropGrid}>
                        {MONTH_NAMES.map((m, i) => {
                          const isToday = i === today.getMonth() && year === today.getFullYear();
                          return (
                            <button
                              key={m}
                              className={`${styles.monthDropItem} ${i === month ? styles.monthDropActive : ""} ${isToday ? styles.monthDropToday : ""}`}
                              onClick={() => { setMonth(i); setMonthDropOpen(false); }}
                            >
                              {m.slice(0, 3)}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <button
                      className={styles.monthDropTodayBtn}
                      onClick={() => {
                        setYear(today.getFullYear());
                        setMonth(today.getMonth());
                        setYearPickerMode(false);
                        setMonthDropOpen(false);
                      }}
                    >
                      Jump to Today
                    </button>
                  </div>
                )}
              </div>
              <button className={styles.navArrow} onClick={nextMonth}>&#8250;</button>
              <button className={styles.todayBtn} onClick={goToday} title="Today">&#9201;</button>
            </div>
          </div>

          <div className={styles.calGrid}>
            <div className={styles.dow}>
              {DOW.map(d => (
                <div key={d} className={`${styles.dowCell} ${d === "Sat" || d === "Sun" ? styles.dowSat : ""}`}>{d}</div>
              ))}
            </div>

            <div className={styles.grid}>
              {cells.map((cell, idx) => {
                const key = dateKey(cell.year, cell.month, cell.day);
                const isToday = key === todayKey;
                const dayEvents = eventsByIdx[idx] || [];

                // De-duplicate and sort by start time (earliest first, no time = last)
                const unique = [];
                const seen = new Set();
                for (const ev of dayEvents) {
                  if (!seen.has(ev.id)) { seen.add(ev.id); unique.push(ev); }
                }
                unique.sort((a, b) => (a.startTime || "99:99").localeCompare(b.startTime || "99:99"));

                const shown = unique.slice(0, 2);
                const extra = unique.length - shown.length;
                const hasEvents = unique.length > 0;
                const bgColor = hasEvents ? (EVENT_BG[unique[0].eventType] || "rgba(246,200,71,.15)") : null;

                const isSelectedDay = selectedEventDays.has(key) && !cell.muted;
                return (
                  <div
                    key={idx}
                    className={`${styles.dayCell} ${cell.muted ? styles.dayMuted : ""} ${isToday ? styles.dayToday : ""} ${hasEvents ? styles.dayHasEvents : ""} ${isSelectedDay ? styles.daySelected : ""}`}
                    style={hasEvents && bgColor && !cell.muted ? { background: bgColor } : undefined}
                    onClick={() => {
                      if (!hasEvents || cell.muted) return;
                      if (unique.length === 1) {
                        setSelectedDay(null);
                        setSelectedEvent(unique[0]);
                      } else {
                        setSelectedEvent(null);
                        setSelectedDay({ date: key, events: unique });
                      }
                    }}
                  >
                    <div className={styles.dayNum}>{cell.day}</div>
                    {hasEvents && (
                      <div className={styles.dayIndicators}>
                        {shown.map((ev) => {
                          const dotColor = EVENT_DOT[ev.eventType] || "#999";
                          return (
                            <div
                              key={ev.id}
                              className={styles.indicator}
                              onClick={(e) => { e.stopPropagation(); setSelectedDay(null); setSelectedEvent(ev); }}
                            >
                              <span className={styles.indicatorDot} style={{ background: dotColor }} />
                              <span className={styles.indicatorText}>{ev.title}</span>
                            </div>
                          );
                        })}
                        {extra > 0 && (
                          <div className={styles.moreTag} onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEvent(null);
                            setSelectedDay({ date: key, events: unique });
                          }}>
                            +{extra} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right panel */}
        <div className={styles.rightPanel}>
          {selectedDay && !selectedEvent ? (
            /* ── Day summary (from "+N more" click) ── */
            <>
              <div className={styles.detailPanel}>
                <div className={styles.detailHeader}>
                  <div className={styles.detailHeaderTitle}>Event Details</div>
                  <button className={styles.detailClose} onClick={() => setSelectedDay(null)}>✕</button>
                </div>
                <div className={styles.detailBody}>
                  <div className={styles.daySummaryDate}>{formatDate(selectedDay.date)}</div>
                  <div className={styles.daySummaryList}>
                    {[...selectedDay.events].sort((a, b) => (a.startTime || "99:99").localeCompare(b.startTime || "99:99")).map(ev => {
                      const dotColor = EVENT_DOT[ev.eventType] || "#999";
                      const creatorName = ev.createdBy
                        ? (allEmployees.find(e => e.code === ev.createdBy)?.name || ev.createdBy)
                        : "Admin";
                      const timeStr = ev.startTime
                        ? (ev.endTime ? `${formatTime(ev.startTime)} - ${formatTime(ev.endTime)}` : formatTime(ev.startTime))
                        : "Whole Day";
                      return (
                        <div
                          key={ev.id}
                          className={styles.daySummaryItem}
                          onClick={() => { setSelectedEvent(ev); }}
                        >
                          <span className={styles.daySummaryDot} style={{ background: dotColor }} />
                          <div className={styles.daySummaryInfo}>
                            <span className={styles.daySummaryTitle}>{ev.title}</span>
                            <span className={styles.daySummaryTime}>{timeStr}</span>
                            <span className={styles.daySummaryCreator}>by {creatorName}</span>
                          </div>
                          <span className={styles.daySummaryType}>{ev.eventType}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className={styles.legend}>
                {EVENT_TYPES.map(t => (
                  <div key={t} className={styles.legendItem}>
                    <div className={`${styles.legendDot} ${styles["dot" + t.replace(/\s/g, "")]}`} />
                    {t}
                  </div>
                ))}
              </div>
            </>
          ) : selectedEvent ? (
            /* ── Single event detail ── */
            <>
              <div className={styles.detailPanel}>
                <div className={styles.detailHeader}>
                  <div className={styles.detailHeaderTitle}>Event Details</div>
                  <button className={styles.detailClose} onClick={() => { setSelectedEvent(null); setSelectedDay(null); }}>✕</button>
                </div>

                <div className={styles.detailBody}>
                  {/* Back to day list if came from "+more" */}
                  {selectedDay && (
                    <button className={styles.backToDay} onClick={() => setSelectedEvent(null)}>
                      ← Back to day
                    </button>
                  )}

                  <div className={styles.detailEventRow}>
                    <div className={styles.detailEventIcon}>📅</div>
                    <div>
                      <div className={styles.detailEventTitle}>
                        {selectedEvent.title}
                        <span className={styles.detailEventTypeBadge}
                              style={{ background: EVENT_BG[selectedEvent.eventType] || "rgba(0,0,0,.08)",
                                       color: EVENT_DOT[selectedEvent.eventType] || "#666" }}>
                          {selectedEvent.eventType}
                        </span>
                      </div>
                      <div className={styles.detailEventDate}>
                        {formatDate(parseDate(selectedEvent.startDate))}
                        {selectedEvent.endDate && parseDate(selectedEvent.endDate) !== parseDate(selectedEvent.startDate)
                          ? ` — ${formatDate(parseDate(selectedEvent.endDate))}`
                          : ""}
                      </div>
                      {(selectedEvent.startTime || selectedEvent.endTime) && (
                        <div className={styles.detailEventTime}>
                          {formatTime(selectedEvent.startTime)}
                          {selectedEvent.endTime ? ` - ${formatTime(selectedEvent.endTime)}` : ""}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.detailCreatedBy}>
                    Created by {selectedEvent.createdBy
                      ? (allEmployees.find(e => e.code === selectedEvent.createdBy)?.name || selectedEvent.createdBy)
                      : "Admin"}
                  </div>

                  {attendeeDetails.length > 0 && (
                    <>
                      <div className={styles.detailSection}>
                        <span className={styles.detailSectionIcon}>👥</span> Attendees
                      </div>
                      <div className={styles.attendeeList}>
                        {attendeeDetails.map(a => (
                          <div key={a.code} className={styles.attendee}>
                            {a.photoUrl
                              ? <img src={a.photoUrl} alt={a.name} className={styles.attendeeAvatarImg} />
                              : <div className={styles.attendeeAvatar} style={{ background: avatarColor(a.name || "?") }}>
                                  {initials(a.name || "?")}
                                </div>
                            }
                            <div className={styles.attendeeName}>{a.name}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {selectedEvent.notes && (
                    <>
                      <div className={styles.detailSection}>
                        <span className={styles.detailSectionIcon}>📝</span> Notes
                      </div>
                      <div className={styles.detailNotes}>{selectedEvent.notes}</div>
                    </>
                  )}

                  {/* ── Leave approval block ─────────────────────── */}
                  {selectedEvent.eventType === "Leave" && selectedEvent.status && (() => {
                    const s = selectedEvent.status;
                    const colors = {
                      PENDING:  { bg: "#fef3c7", fg: "#92400e", label: "Pending" },
                      APPROVED: { bg: "#dcfce7", fg: "#166534", label: "Approved" },
                      REJECTED: { bg: "#fee2e2", fg: "#991b1b", label: "Rejected" },
                    }[s] || { bg: "#e5e7eb", fg: "#374151", label: s };
                    const deciderName = selectedEvent.decidedBy
                      ? (allEmployees.find(e => e.code === selectedEvent.decidedBy)?.name
                          || selectedEvent.decidedBy)
                      : null;
                    const decidedAtStr = selectedEvent.decidedAt
                      ? new Date(selectedEvent.decidedAt).toLocaleString()
                      : "";
                    const manager = canDecide(selectedEvent);
                    return (
                      <>
                        <div className={styles.detailSection}>
                          <span className={styles.detailSectionIcon}>🗂</span> Approval
                        </div>
                        <div style={{ padding: "6px 0 12px" }}>
                          <span style={{
                            display: "inline-block", fontSize: 11, fontWeight: 700,
                            padding: "3px 10px", borderRadius: 999, letterSpacing: ".4px",
                            background: colors.bg, color: colors.fg,
                          }}>{colors.label.toUpperCase()}</span>
                        </div>
                        {s !== "PENDING" && deciderName && (
                          <div style={{ fontSize: 12, color: "#555", marginBottom: 8 }}>
                            {s === "APPROVED" ? "Approved" : "Rejected"} by{" "}
                            <strong>{deciderName}</strong>
                            {decidedAtStr ? <> · {decidedAtStr}</> : null}
                          </div>
                        )}
                        {s !== "PENDING" && selectedEvent.decisionNote && (
                          <div style={{
                            fontSize: 12, color: "#374151", background: "#f9fafb",
                            borderRadius: 8, padding: "8px 10px", marginBottom: 10,
                            whiteSpace: "pre-wrap",
                          }}>
                            {selectedEvent.decisionNote}
                          </div>
                        )}
                        {s === "PENDING" && (() => {
                          // Resolve the approver from the applicant's
                          // Employee.reportsTo. The value may be an
                          // employee code (then show the profile name
                          // + job role) or one of the role tokens
                          // "Admin" / "Owner".
                          const applicant = allEmployees.find(e => e.code === selectedEvent.createdBy);
                          const reports   = applicant?.reportsTo;
                          let approverName  = null;
                          let approverTitle = null;
                          if (reports === "Admin") {
                            approverName  = "Admin";
                            approverTitle = "any admin-tier user";
                          } else if (reports === "Owner") {
                            approverName  = "Owner";
                            approverTitle = "Owner / SuperAdmin";
                          } else if (reports) {
                            const mgr = allEmployees.find(e => e.code === reports);
                            approverName  = mgr?.name || reports;
                            approverTitle = mgr?.role || null;
                          }
                          return (
                            <div style={{
                              fontSize: 12, color: "#374151",
                              background: "#f9fafb", borderRadius: 8,
                              padding: "8px 10px", marginBottom: 10,
                            }}>
                              <div style={{ fontSize: 11, color: "#6b7280",
                                            textTransform: "uppercase", letterSpacing: ".4px",
                                            fontWeight: 700, marginBottom: 2 }}>
                                Needs approval from
                              </div>
                              {approverName ? (
                                <div>
                                  <strong>{approverName}</strong>
                                  {approverTitle ? <> — {approverTitle}</> : null}
                                </div>
                              ) : (
                                <div style={{ color: "#9ca3af" }}>
                                  No manager assigned on the applicant's record.
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {s === "PENDING" && manager && (
                          <div style={{ display: "grid", gap: 8, marginBottom: 8 }}>
                            <textarea
                              className={styles.input}
                              rows={3}
                              placeholder="Optional note (required on reject is good practice)..."
                              value={decisionNote}
                              onChange={e => setDecisionNote(e.target.value)}
                              style={{ resize: "vertical", fontFamily: "inherit" }}
                            />
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                className={styles.editBtn}
                                style={{ background: "#15803d", color: "#fff", flex: 1 }}
                                disabled={decisionSaving}
                                onClick={() => handleDecide("APPROVED")}
                              >
                                {decisionSaving ? "Saving..." : "✓ Approve"}
                              </button>
                              <button
                                className={styles.deleteBtn}
                                style={{ flex: 1 }}
                                disabled={decisionSaving}
                                onClick={() => handleDecide("REJECTED")}
                              >
                                {decisionSaving ? "Saving..." : "✕ Reject"}
                              </button>
                            </div>
                          </div>
                        )}
                        {s === "PENDING" && !manager && (
                          <div style={{ fontSize: 12, color: "#777", marginBottom: 8 }}>
                            Only the approver listed above can decide this request.
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Edit / Delete: owner + admin as before, plus the
                      manager on their reports' Leave events. Once a
                      Leave is APPROVED the applicant loses delete
                      rights — only admin-tier can remove an approved
                      leave so employees can't quietly erase a decision. */}
                  {(() => {
                    const isOwnEvent = selectedEvent.createdBy && selectedEvent.createdBy === user?.employeeCode;
                    const isApprovedLeave = selectedEvent.eventType === "Leave"
                      && selectedEvent.status === "APPROVED";
                    const canEdit   = isAdmin || isOwnEvent;
                    // Once approved, only admin-tier can delete so a
                      // record of the decision can't be quietly erased
                      // — neither the applicant nor the deciding manager.
                      const canDelete = isApprovedLeave
                      ? isAdmin
                      : (isAdmin
                          || isOwnEvent
                          || (selectedEvent.eventType === "Leave" && canDecide(selectedEvent)));
                    if (!canEdit && !canDelete) return null;
                    return (
                      <div className={styles.detailActions}>
                        {canEdit && (
                          <button className={styles.editBtn} onClick={() => setModalEvent(selectedEvent)}>Edit</button>
                        )}
                        {canDelete && (
                          <button className={styles.deleteBtn} onClick={handleDelete}>Delete</button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className={styles.legend}>
                {EVENT_TYPES.map(t => (
                  <div key={t} className={styles.legendItem}>
                    <div className={`${styles.legendDot} ${styles["dot" + t.replace(/\s/g, "")]}`} />
                    {t}
                  </div>
                ))}
              </div>
            </>
          ) : (
            /* ── Empty state ── */
            <>
              <div className={styles.panelEmpty}>
                <div className={styles.panelEmptyIcon}>💡</div>
                <div className={styles.panelEmptyTitle}>No Event Selected</div>
                <div className={styles.panelEmptyText}>
                  Click on an event in the calendar<br />to see its details.
                </div>
              </div>
              <div className={styles.legend}>
                {EVENT_TYPES.map(t => (
                  <div key={t} className={styles.legendItem}>
                    <div className={`${styles.legendDot} ${styles["dot" + t.replace(/\s/g, "")]}`} />
                    {t}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {modalEvent !== undefined && (
        <EventModal
          event={modalEvent}
          onClose={() => setModalEvent(undefined)}
          onSaved={() => {
            setModalEvent(undefined);
            setSelectedEvent(null);
            loadEvents();
            loadApprovals();
            // A new Leave event is auto-PENDING — let the Topbar bell
            // surface it immediately for the manager / admin.
            window.dispatchEvent(new CustomEvent("approvals:changed"));
          }}
          allEmployees={allEmployees}
          isAdmin={isAdmin}
          currentUser={user}
          events={events}
          year={year}
        />
      )}

      {/* Leave Approval Records modal */}
      {recordsOpen && (
        <RecordsModal
          records={records}
          loading={recordsLoading}
          employees={allEmployees}
          onClose={() => setRecordsOpen(false)}
          onOpenEvent={(ev) => {
            setRecordsOpen(false);
            setSelectedEvent(ev);
            setSelectedDay(null);

            // Also point at the applicant in the left sidebar so the
            // operator can see whose record they're about to act on.
            // We gate on visibility:
            //   · Admin / Owner / SuperAdmin → can always select.
            //   · Manager → only if the applicant is in their reports
            //     (or is themselves).
            //   · Otherwise → don't change selectedCode; show a toast.
            const applicantCode = ev.createdBy || "";
            const visible =
              !applicantCode ||
              isAdmin ||
              applicantCode === user?.employeeCode ||
              isManagerOfApplicant(applicantCode);

            if (visible) {
              if (applicantCode) setSelectedCode(applicantCode);
              setTeamWarning(null);
            } else {
              const applicant = allEmployees.find(e => e.code === applicantCode);
              setTeamWarning({
                name: applicant?.name || applicantCode || "This employee",
                code: applicantCode,
              });
            }
          }}
        />
      )}

      {/* "Not on your team" toast — appears when a manager opens a
          record whose applicant doesn't report to them. The leave's
          right-rail details + day-cell highlight still render, so they
          can see context; only the sidebar selection is suppressed
          because the applicant isn't in their visible employee list. */}
      {teamWarning && (
        <div className={styles.teamWarnToast} role="status" aria-live="polite">
          <span className={styles.teamWarnIcon} aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </span>
          <div className={styles.teamWarnBody}>
            <div className={styles.teamWarnTitle}>{teamWarning.name} isn't on your team</div>
            <div className={styles.teamWarnText}>
              You can review this leave's details, but they don't appear in your employee list because they don't report to you.
            </div>
          </div>
          <button type="button"
                  className={styles.teamWarnClose}
                  onClick={() => setTeamWarning(null)}
                  aria-label="Dismiss">×</button>
        </div>
      )}
    </div>
  );
}

function RecordsModal({ records, loading, employees, onClose, onOpenEvent }) {
  const [filter, setFilter] = useState("ALL");
  const filtered = (records || []).filter(r => filter === "ALL" || r.status === filter);
  const nameOf = (code) => {
    if (!code) return "Admin";
    return employees.find(e => e.code === code)?.name || code;
  };
  const statusChip = (s) => {
    const map = {
      PENDING:  { bg: "#fef3c7", fg: "#92400e" },
      APPROVED: { bg: "#dcfce7", fg: "#166534" },
      REJECTED: { bg: "#fee2e2", fg: "#991b1b" },
    };
    const c = map[s] || { bg: "#e5e7eb", fg: "#374151" };
    return (
      <span style={{
        display: "inline-block", fontSize: 10, fontWeight: 700,
        padding: "2px 8px", borderRadius: 999, letterSpacing: ".4px",
        background: c.bg, color: c.fg,
      }}>{s}</span>
    );
  };
  return createPortal(
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 24,
      }}>
      <div style={{
        background: "#fff", borderRadius: 14, width: "min(900px, 100%)",
        maxHeight: "85vh", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 60px rgba(0,0,0,.28), 0 2px 8px rgba(0,0,0,.06)",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: "1px solid #eee",
        }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Leave Approval Records</div>
          <button onClick={onClose}
                  style={{ background: "#f3f4f6", border: "none", borderRadius: 999,
                           width: 28, height: 28, cursor: "pointer", fontSize: 14 }}>✕</button>
        </div>
        <div style={{ padding: "10px 20px", display: "flex", gap: 8, borderBottom: "1px solid #f1f5f9" }}>
          {["ALL", "PENDING", "APPROVED", "REJECTED"].map(k => (
            <button key={k} onClick={() => setFilter(k)}
                    style={{
                      padding: "4px 12px", borderRadius: 999, border: "1px solid #e5e7eb",
                      cursor: "pointer", fontSize: 12, fontWeight: 600,
                      background: filter === k ? "#111827" : "#fff",
                      color:      filter === k ? "#fff"    : "#374151",
                    }}>{k}</button>
          ))}
          <div style={{ marginLeft: "auto", fontSize: 11, color: "#6b7280", alignSelf: "center" }}>
            {loading ? "Loading..." : `${filtered.length} record(s)`}
          </div>
        </div>
        <div style={{ overflow: "auto", flex: 1 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
              No {filter === "ALL" ? "" : filter.toLowerCase() + " "}records.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9fafb", color: "#374151", fontSize: 11,
                             textAlign: "left", textTransform: "uppercase", letterSpacing: ".4px" }}>
                  <th style={{ padding: "10px 12px" }}>Applicant</th>
                  <th style={{ padding: "10px 12px" }}>Title</th>
                  <th style={{ padding: "10px 12px" }}>Dates</th>
                  <th style={{ padding: "10px 12px" }}>Status</th>
                  <th style={{ padding: "10px 12px" }}>Decided by / at</th>
                  <th style={{ padding: "10px 12px" }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {filtered
                  .slice()
                  .sort((a, b) => (b.decidedAt || b.startDate).localeCompare(a.decidedAt || a.startDate))
                  .map(r => {
                    const range = r.endDate && r.endDate !== r.startDate
                      ? `${r.startDate} → ${r.endDate}`
                      : r.startDate;
                    const decided = r.decidedBy
                      ? `${nameOf(r.decidedBy)}${r.decidedAt ? " · " + new Date(r.decidedAt).toLocaleString() : ""}`
                      : "—";
                    return (
                      <tr key={r.id}
                          onClick={() => onOpenEvent(r)}
                          style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <td style={{ padding: "10px 12px", fontWeight: 600 }}>{nameOf(r.createdBy)}</td>
                        <td style={{ padding: "10px 12px" }}>{r.title}</td>
                        <td style={{ padding: "10px 12px", color: "#4b5563" }}>{range}</td>
                        <td style={{ padding: "10px 12px" }}>{statusChip(r.status)}</td>
                        <td style={{ padding: "10px 12px", color: "#4b5563", fontSize: 12 }}>{decided}</td>
                        <td style={{ padding: "10px 12px", color: "#374151",
                                     maxWidth: 240, whiteSpace: "pre-wrap" }}>
                          {r.decisionNote || <span style={{ color: "#9ca3af" }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
